// breather/scene/Blob.ts
// -----------------------------------------------------------------------------
// This file defines the Blob class: the central visual object representing
// breath, pressure, and internal flow in the Breather app.
//
// Responsibilities:
// - Own the mesh geometry (sphere-based)
// - Own the shader material and all its uniforms
// - Expose safe, semantic setters for main.tsx / Sequencer
// - Act as the single source of truth for blob appearance
// -----------------------------------------------------------------------------

// Three.js core imports
import {
  Mesh,            // Renderable mesh object
  ShaderMaterial,  // Custom GLSL-driven material
  SphereGeometry,  // Base geometry (smooth sphere, high subdivision)
  Color,           // RGB color utility (used for phase color)
  DoubleSide,      // Render both sides of the surface
  Vector2          // 2D vector (used for highlight positioning)
} from 'three'

// Raw GLSL imports (Vite loads these as strings)
import vert from '../shaders/blob.vert.glsl?raw'
import frag from '../shaders/blob.frag.glsl?raw'

// SceneGraph abstraction (owns the THREE.Scene, camera, renderer, etc.)
import { SceneGraph } from './SceneGraph'

// -----------------------------------------------------------------------------
// Blob
// -----------------------------------------------------------------------------
// This class encapsulates the breathing “blob”:
//
// - Geometry: a smooth sphere scaled into an organic form
// - Material: custom vertex + fragment shaders
// - Uniforms: all visual tuning knobs exposed to main.tsx
//
// The Blob itself does NOT know about breathing timing or phases;
// those are driven externally by Sequencer / GSAP.
// -----------------------------------------------------------------------------
export class Blob {

  // The THREE.Mesh instance added to the scene
  mesh: Mesh

  // The ShaderMaterial controlling rendering
  mat: ShaderMaterial

  // Uniforms object passed into ShaderMaterial
  // Typed as `any` because Three’s uniform typing is verbose
  uniforms: any

  // Shared color object representing the current phase color.
  // This is mutated over time (GSAP tweens its r/g/b values).
  colorPhase = new Color('#3b82f6')

  // ---------------------------------------------------------------------------
  // Constructor
  // ---------------------------------------------------------------------------
  constructor(g: SceneGraph) {

    // High-resolution sphere geometry:
    // - Much smoother than Icosahedron
    // - Avoids faceting or polygon artifacts under lighting
    const geo = new SphereGeometry(
      1,    // radius
      128,  // width segments
      128   // height segments
    )

    // -------------------------------------------------------------------------
    // Shader uniforms
    // -------------------------------------------------------------------------
    // These values are read by both the vertex and fragment shaders.
    // Many of them are animated over time by GSAP in main.tsx.
    //
    // IMPORTANT:
    // - Defaults here represent the “neutral” state
    // - Live values usually come from Sequencer phase tweens
    // -------------------------------------------------------------------------
    this.uniforms = {

      // Time accumulator (seconds)
      // Drives noise animation and subtle shimmer
      uTime:         { value: 0 },

      // Idle wobble amplitude
      // Higher = more liquid / blobby motion
      uIdleAmp:      { value: 0.03 },

      // Vertical elongation bias
      // + values bulge top (inhale)
      // - values bulge bottom (exhale)
      uElongate:     { value: 0.2 },

      // Vertical position of the internal “air band”
      // Normalized 0..1 from bottom to top of blob
      uLightY:       { value: 0.45 },

      // Current phase color (shared Color object)
      // Mutated directly for smooth color blending
      uColorPhase:   { value: this.colorPhase },

      // Subsurface / internal glow tint
      // Typically a soft white or slightly tinted highlight color
      uSSSColor:     { value: new Color('#F8F3FF') },

      // Fresnel exponent
      // Controls how tight or broad the rim highlight is
      uFresnelPower: { value: 2.0 },

      // Fake environment intensity
      // Used to lift or flatten the material response
      uEnvIntensity: { value: 1.0 },

      // Spatial scale of procedural noise
      // Larger values = bigger wobble features
      uNoiseScale:   { value: 1.0 },

      // Speed of noise animation
      // Controls how “alive” the surface feels
      uNoiseSpeed:   { value: 0.25 },

      // Optional bloom multiplier
      // Read by fragment shader / post FX
      uBloomBoost:   { value: 0.0 },

      // -----------------------------------------------------------------------
      // Shape / internal flow controls
      // -----------------------------------------------------------------------

      // Maximum radial bulge strength
      // Think of this as “pressure” or “water balloon fullness”
      uBulgeMax:  { value: 0.18 },

      // Width of the internal glow band
      // Larger = thicker / softer air region
      uAirWidth:  { value: 0.18 }, // band radius in normalized latitude space (0..1-ish)

      // Intensity of the internal glow band
      // Higher = stronger sense of internal pressure
      uAirWeight: { value: 0.40 },

      // -----------------------------------------------------------------------
      // Specular-style highlight controls (gel glint)
      // -----------------------------------------------------------------------

      // Center of highlight in normal-space (N.x, N.y)
      // Controls where the highlight sits on the blob surface
      uHighlightCenter:      { value: new Vector2(0.32, 0.80) },

      // Inner radius of the highlight ellipse
      // Smaller = thinner, sharper core
      uHighlightRadiusInner: { value: 0.19 },

      // Outer radius of the highlight ellipse
      // Larger = longer / softer highlight
      uHighlightRadiusOuter: { value: 0.40 },

      // Overall highlight brightness
      uHighlightIntensity:   { value: 0.09 },

      // Vertical stretch of the highlight ellipse
      // >1 = taller highlight, <1 = squatter
      uHighlightAspect:      { value: 1.25 },
    }

    // -------------------------------------------------------------------------
    // Shader material
    // -------------------------------------------------------------------------
    this.mat = new ShaderMaterial({
      uniforms: this.uniforms,
      vertexShader: vert,
      fragmentShader: frag,
      transparent: true,
      side: DoubleSide
    })

    // -------------------------------------------------------------------------
    // Mesh setup
    // -------------------------------------------------------------------------
    this.mesh = new Mesh(geo, this.mat)

    // Non-uniform scaling to create a more organic silhouette:
    // Slightly taller than wide by default
    this.mesh.scale.set(
      0.60, // X
      0.78, // Y
      0.60  // Z
    )

    // Add to the scene graph
    g.scene.add(this.mesh)
  }

  // ---------------------------------------------------------------------------
  // Convenience accessors
  // ---------------------------------------------------------------------------
  // These methods provide semantic, safe ways for main.tsx and Sequencer
  // to influence the blob without reaching directly into uniforms everywhere.
  // ---------------------------------------------------------------------------

  // Set the phase color using a hex string
  // (internally mutates the shared Color object)
  setPhaseColor(hex: string) {
    this.colorPhase.set(hex)
  }

  // Set vertical position of the air band
  setLightY(v: number) {
    this.uniforms.uLightY.value = v
  }

  // Set elongation bias (top vs bottom bulge)
  setElongate(v: number) {
    this.uniforms.uElongate.value = v
  }

  // Set pressure / idle wobble strength
  // Maps a normalized 0..1 input to a usable amplitude range
  setPressure(p: number) {
    this.uniforms.uIdleAmp.value = 0.02 + p * 0.02
  }

  // Advance time uniform (called once per frame)
  update(dt: number) {
    this.uniforms.uTime.value += dt
  }
}
